/**
 * Lógica pura de la pantalla de calibración (sin React) para poder testearla.
 * La usa StaffCalibracionPage para listar factores y validar antes de publicar.
 */
import type { CalibracionDoc, FactorCalibracion } from '../api/calibracion.js';
import {
  DESCRIPCION_PARAMETROS_CHEQUEO_PLANO,
  RANGO_PARAMETROS_CHEQUEO_PLANO,
  DESCRIPCION_PARAMETROS_CAMBIO_DE_PLANO,
  RANGO_PARAMETROS_CAMBIO_DE_PLANO,
  DESCRIPCION_PARAMETROS_MEDICION_DWG,
  PARES_MIN_MAX_MEDICION_DWG,
  RANGO_PARAMETROS_MEDICION_DWG,
  RANGO_PARAMETROS_MEDICION_IFC,
} from '@supernova/shared';

/** Factores gateables del catálogo (proyecto + concepto rendimiento). */
export function gateables(doc: CalibracionDoc): FactorCalibracion[] {
  return [
    ...(doc.factores.nivel_proyecto?.factores ?? []),
    ...(doc.factores.nivel_concepto?.factores_rendimiento ?? []),
  ];
}

/**
 * Validación semántica antes de publicar (barandales para no romper el motor
 * con un fat-finger). El backend valida la forma; aquí la coherencia.
 * Devuelve la lista de errores (vacía = OK para publicar).
 */
export function validarCalibracion(doc: CalibracionDoc): string[] {
  const errs: string[] = [];
  const tope = doc.combinacion.tope;
  if (!(tope <= 0)) errs.push('El tope debe ser negativo o cero (es un castigo, ej. −45%).');
  if (tope < -1) errs.push('El tope no puede ser menor que −100%.');

  const {
    match_verde_min: verde, match_rojo_max: rojo,
    apu_cobertura_verde_min: covVerde, apu_cobertura_roja_max: covRojo,
    apu_reduccion_roja_min: redRoja, apu_reduccion_amarilla_min: redAmarilla,
  } = doc.umbrales;
  for (const [label, v] of [
    ['Match verde', verde], ['Match rojo', rojo],
    ['Cobertura verde', covVerde], ['Cobertura roja', covRojo],
    ['Reducción roja', redRoja], ['Reducción amarilla', redAmarilla],
  ] as const) {
    if (!(v >= 0 && v <= 1)) errs.push(`"${label}" debe estar entre 0 y 1.`);
  }
  // Vínculo nota de lámina → concepto (OPUS ci): fracciones 0–1. Las calibraciones guardadas antes de
  // que existiera una clave la completan los defaults, así que una ausente no es error.
  for (const [label, v] of [
    ['Especificación con confianza alta', doc.umbrales.spec_vinculo_alta_min],
    ['Ventaja sobre el segundo candidato', doc.umbrales.spec_vinculo_alta_margen],
    ['Especificación con confianza media', doc.umbrales.spec_vinculo_media_min],
    ['Mismo elemento con otra palabra', doc.umbrales.spec_vinculo_mismo_elemento_min],
    ['«Proponer más»: misma necesidad', doc.umbrales.necesidad_repetida_min],
  ] as const) {
    if (v != null && !(v >= 0 && v <= 1)) errs.push(`"${label}" debe estar entre 0 y 1.`);
  }
  if (verde < rojo) errs.push('El umbral verde debe ser ≥ que el rojo (verde manda a directo, rojo a APU).');
  if (covVerde < covRojo) errs.push('La cobertura verde del APU debe ser ≥ que la roja.');
  if (redRoja < redAmarilla) errs.push('La reducción roja debe ser ≥ que la amarilla (el rojo es el castigo mayor).');

  // TTL del caché de precios (AI-8): días enteros, no fracción 0–1.
  const ttl = doc.umbrales.market_price_cache_ttl_days;
  if (ttl != null && (!Number.isInteger(ttl) || ttl < 0 || ttl > 365)) {
    errs.push('El TTL del caché de precios debe ser un número entero de días entre 0 y 365 (0 = desactivado).');
  }

  // Vigencia del tipo de cambio (migración 074): horas enteras.
  const fxTtl = doc.umbrales.fx_rate_ttl_hours;
  if (fxTtl != null && (!Number.isInteger(fxTtl) || fxTtl < 0 || fxTtl > 8760)) {
    errs.push('La vigencia del tipo de cambio debe ser un número entero de horas entre 0 y 8760 (0 = consultar siempre).');
  }

  // Cordura de precios: multiplicador sobre el insumo más caro del catálogo.
  // Por debajo de 1 descartaría precios que el catálogo sí registra.
  const cordura = doc.umbrales.precio_cordura_factor;
  if (cordura != null && cordura !== 0 && (!(cordura >= 1) || cordura > 100)) {
    errs.push('El factor de cordura de precios debe ser 0 (desactivado) o un número entre 1 y 100.');
  }

  // Piso de cordura: divisor sobre el insumo más BARATO del catálogo. Por debajo de
  // 1 el piso quedaría POR ENCIMA del precio más bajo que el catálogo registra, y
  // entonces marcaría precios que el propio catálogo admite — la garantía medida
  // ("ningún precio legítimo del catálogo se rechaza") depende de este límite.
  const piso = doc.umbrales.precio_piso_factor;
  if (piso != null && piso !== 0 && (!(piso >= 1) || piso > 100)) {
    errs.push('El factor del piso de precios debe ser 0 (desactivado) o un número entre 1 y 100.');
  }

  // Reconciliación de la lectura del catálogo: cantidades enteras, no fracciones.
  const tol = doc.umbrales.reconciliacion_tolerancia_renglones;
  if (tol != null && (!Number.isInteger(tol) || tol < 0 || tol > 50)) {
    errs.push('La tolerancia por página debe ser un número entero de renglones entre 0 y 50 (0 = cualquier diferencia manda a releer).');
  }
  const maxRel = doc.umbrales.reconciliacion_max_relecturas;
  if (maxRel != null && (!Number.isInteger(maxRel) || maxRel < 0 || maxRel > 500)) {
    errs.push('El tope de relecturas debe ser un número entero de páginas entre 0 y 500 (0 = sin tope).');
  }
  const exigir = doc.umbrales.reconciliacion_exigir_cierre;
  if (exigir != null && exigir !== 0 && exigir !== 1) {
    errs.push('Exigir que el listado cierre es 1 (no entregar el presupuesto) o 0 (entregarlo con el renglón marcado).');
  }

  // Medición de IFC: tolerancias en VECES (>= 1: por debajo nada mediría "lo mismo" que la geometría,
  // ni el propio valor exacto), la fracción de cara plana entre 0 y 1 y las densidades por familia
  // dentro de su rango físico.
  const nombres: Record<keyof typeof RANGO_PARAMETROS_MEDICION_IFC, string> = {
    ifc_ventana_area: 'La tolerancia de área de IFC',
    ifc_ventana_longitud: 'La tolerancia de largo de IFC',
    ifc_ventana_volumen: 'La tolerancia de volumen de IFC',
    ifc_cobertura_cara_plana: 'La fracción mínima de cara plana de IFC',
    ifc_ventana_peso: 'La tolerancia de peso de IFC',
    ifc_densidad_acero: 'La densidad del acero (kg/m³)',
    ifc_densidad_concreto: 'La densidad del concreto (kg/m³)',
    ifc_densidad_madera: 'La densidad de la madera (kg/m³)',
    ifc_densidad_aluminio: 'La densidad del aluminio (kg/m³)',
    ifc_densidad_vidrio: 'La densidad del vidrio (kg/m³)',
    ifc_densidad_mamposteria: 'La densidad de la mampostería (kg/m³)',
    ifc_densidad_yeso: 'La densidad del yeso (kg/m³)',
    ifc_densidad_concreto_ligero: 'La densidad del concreto ligero (kg/m³)',
    ifc_forma_lineal_min: 'La forma lineal mínima de IFC',
    ifc_aviso_tamano_mb: 'El tamaño de IFC que pide confirmación (MB)',
    ifc_forma_plana_min: 'La forma plana mínima de IFC',
    ifc_nivel_tolerancia: 'La tolerancia de niveles deducidos de IFC',
  };
  for (const k of Object.keys(RANGO_PARAMETROS_MEDICION_IFC) as (keyof typeof RANGO_PARAMETROS_MEDICION_IFC)[]) {
    const v = doc.umbrales[k];
    const [mn, mx] = RANGO_PARAMETROS_MEDICION_IFC[k];
    if (v != null && (!Number.isFinite(v) || v < mn || v > mx)) errs.push(`${nombres[k]} debe estar entre ${mn} y ${mx}.`);
  }

  // Medición de planos DWG/DXF: rangos y etiquetas en @supernova/shared (parametrosMedicionDwg.ts), y
  // los pares mínimo/máximo en orden.
  for (const k of Object.keys(RANGO_PARAMETROS_MEDICION_DWG) as (keyof typeof RANGO_PARAMETROS_MEDICION_DWG)[]) {
    const v = doc.umbrales[k];
    const [mn, mx] = RANGO_PARAMETROS_MEDICION_DWG[k];
    if (v != null && (!Number.isFinite(v) || v < mn || v > mx)) {
      errs.push(`DWG — ${DESCRIPCION_PARAMETROS_MEDICION_DWG[k].etiqueta} debe estar entre ${mn} y ${mx}.`);
    }
  }
  for (const [kMin, kMax] of PARES_MIN_MAX_MEDICION_DWG) {
    const a = doc.umbrales[kMin];
    const b = doc.umbrales[kMax];
    if (a != null && b != null && a > b) {
      errs.push(`DWG — ${DESCRIPCION_PARAMETROS_MEDICION_DWG[kMin].etiqueta} no puede ser mayor que ${DESCRIPCION_PARAMETROS_MEDICION_DWG[kMax].etiqueta}.`);
    }
  }

  // «¿Este plano sirve para presupuestar?»: rangos y etiquetas en @supernova/shared (chequeoDelPlano.ts).
  for (const k of Object.keys(RANGO_PARAMETROS_CHEQUEO_PLANO) as (keyof typeof RANGO_PARAMETROS_CHEQUEO_PLANO)[]) {
    const v = doc.umbrales[k];
    const [mn, mx] = RANGO_PARAMETROS_CHEQUEO_PLANO[k];
    if (v != null && (!Number.isFinite(v) || v < mn || v > mx)) {
      errs.push(`Chequeo del plano — ${DESCRIPCION_PARAMETROS_CHEQUEO_PLANO[k].etiqueta} debe estar entre ${mn} y ${mx}.`);
    }
  }
  // «Cambió el plano»: rangos y etiquetas en @supernova/shared (cambioDePlano.ts). Sin regla cruzada.
  for (const k of Object.keys(RANGO_PARAMETROS_CAMBIO_DE_PLANO) as (keyof typeof RANGO_PARAMETROS_CAMBIO_DE_PLANO)[]) {
    const v = doc.umbrales[k];
    const [mn, mx] = RANGO_PARAMETROS_CAMBIO_DE_PLANO[k];
    if (v != null && (!Number.isFinite(v) || v < mn || v > mx)) {
      errs.push(`Cambió el plano — ${DESCRIPCION_PARAMETROS_CAMBIO_DE_PLANO[k].etiqueta} debe estar entre ${mn} y ${mx}.`);
    }
  }
  {
    const bien = doc.umbrales.chequeo_listos_bien_pct;
    const cuidado = doc.umbrales.chequeo_listos_cuidado_pct;
    if (bien != null && cuidado != null && cuidado > bien) {
      errs.push(`Chequeo del plano — ${DESCRIPCION_PARAMETROS_CHEQUEO_PLANO.chequeo_listos_cuidado_pct.etiqueta} no puede ser mayor que ${DESCRIPCION_PARAMETROS_CHEQUEO_PLANO.chequeo_listos_bien_pct.etiqueta}.`);
    }
  }

  for (const f of gateables(doc)) {
    const a = f.ajuste;
    if (!a) continue;
    const { rango_min_pct: mn, rango_max_pct: mx, default_pct: def } = a;
    if (mn != null && mx != null && mn > mx) errs.push(`${f.id}: rango min (${mn}) no puede ser mayor que max (${mx}).`);
    if (def != null && mn != null && def < mn) errs.push(`${f.id}: default (${def}) está por debajo del rango min (${mn}).`);
    if (def != null && mx != null && def > mx) errs.push(`${f.id}: default (${def}) está por encima del rango max (${mx}).`);
  }
  return errs;
}
